import {useVeriflytTicketEditorState, VeriflytTicketEditorApi} from "../veriflyt-ticket-editor";
import React, {useContext} from "react";
import {Button, Collapse, Typography} from "antd";
import {getCollapsePanelKey} from "../util";
import VeriflytTicketComponent from "./ticket-form";
import NonIdealState from "../../../NonIdealState";
import VeriflytTicketEditorHeader from "./header";
import useRegisterPersonToVeriflyt from "../register-to-veriflyt";
import './editor.style.less';
import {OrgDataContext} from "../../../../context/orgData";
import {DataflytContactEmail, DataflytContactPhone} from 'components/dataflyt-contact';
import CenteredLoadingScreen from "../../../CenteredLoadingScreen";
import 'css/Collapse.less';
import useBreakpoints from "../../../../hooks/useBreakpoints";

export type VeriflytTicketEditorProps = ({
    api?: VeriflytTicketEditorApi
} | {
    personID: string,
    customerID?: string
}) & {
    readOnly?: boolean,
    compact?: boolean
}

const VeriflytTicketEditorInternal: React.FC<VeriflytTicketEditorProps> = (props) => {

    const orgData = useContext(OrgDataContext);
    const bp = useBreakpoints();

    // Switch between uncontrolled and controlled modes
    let api = useVeriflytTicketEditorState('personID' in props ? props.personID : null, { readOnly: props.readOnly });
    api = 'api' in props ? props.api : api;

    const registerToVeriflyt = useRegisterPersonToVeriflyt(api);

    let compact = props.compact;

    if (typeof props.compact !== 'boolean'){
        // Determine compact mode based on breakpoints
        compact = bp.max('medium');
    }

    const { state } = api;

    const usedTicketTemplateIds = new Map<string, null>();

    const ticketForms = Array.from(state.formState.values());

    const existingForms = ticketForms
        .filter((data) => data._id);

    const newForms = ticketForms
        .filter((data) => !data._id);

    const combinedTicketForms = [
        ...newForms,
        ...existingForms
    ]

    combinedTicketForms.forEach((formData) => {
        if (formData.ticketTemplateIDAndName?.value?.key){
            usedTicketTemplateIds.set(formData.ticketTemplateIDAndName.value.key, null);
        }
    })

    const tickets = combinedTicketForms
        .map((data) => {

            const header = <VeriflytTicketEditorHeader data={data} api={api} compact={compact} />

            return (
                <Collapse.Panel key={getCollapsePanelKey(data)} header={header}>
                    <VeriflytTicketComponent
                        data={data}
                        ref={(ref) => api.addFormRef(data.internalId, ref)}
                        onChange={(newData) => {
                            api.updateTicket(newData);
                        }}
                        readOnly={api.readOnly}
                    />
                </Collapse.Panel>
            )
        })

    if (!api.organizationIsConfigured){

        const organizationName = orgData.organization?.name;

        return (
            <NonIdealState
                title={`Your organization ${organizationName} is not configured for VERIFLYT`}
                description={
                    <>
                        <p>To view records, please contact DataFlyt to register your organization.</p>
                        <p>Support phone: <DataflytContactPhone /></p>
                        <p>Support email: <DataflytContactEmail /></p>
                    </>
                }
                icon="exclamation-circle"
                type="warning"
            />
        )
    }

    if (api.loading){
        return (
            <CenteredLoadingScreen />
        )
    }

    if (!api.personIsConfigured){
        return (
            <NonIdealState
                title="This person is not configured for VERIFLYT"
                description={
                    <>
                        <p>To enable VeriFlyt for this person, register this person for VeriFlyt.</p>
                        {!api.readOnly ? (
                            <Button
                                type="primary"
                                loading={registerToVeriflyt.loading}
                                onClick={() => {
                                    registerToVeriflyt.register()
                                        .then(() => {
                                            api.resetTickets();
                                        })
                                }}
                            >Register for VeriFlyt</Button>
                        ) : null}
                    </>
                }
                icon="exclamation-circle"
                type="warning"
            />
        )
    }

    if (!tickets.length){
        return (
            <NonIdealState
                title="Add Certificate Records"
                description={api.readOnly ? "No certificate records" : "Click \"Add\" to add a certificate record"}
                icon="profile"
                type="primary"
            />
        )
    }

    let currentHeader = <span>
        <Typography.Text strong>Current Records</Typography.Text>
        <span style={{ marginLeft: 6 }}>
            <Typography.Text type="secondary">
                ({tickets.length})
            </Typography.Text>
        </span>
    </span>

    return (
        <>
            <Collapse className="compliance-editor" defaultActiveKey="tickets">
                <Collapse.Panel header={currentHeader} key="tickets">
                    <Collapse
                        className="compliance-editor-tickets mc-collapse-rounded"
                        activeKey={state.collapseActiveKeys}
                        onChange={api.setCollapseKeys}
                    >
                        {tickets}
                    </Collapse>
                </Collapse.Panel>
                {/* Historical records panel intentionally excluded */}
            </Collapse>
        </>
    )
}

export default VeriflytTicketEditorInternal;
